import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "@/lib/api";
import { Card, KorwilBadge, Progress, SectionTitle, TableShell, Td, Th } from "@/components/Shared";

const METRICS = [
  ["technician_coverage", "Technician Coverage"],
  ["unit_coverage", "Unit Coverage"],
  ["completion_rate", "Completion Rate"],
  ["on_time_rate", "On-Time Rate"],
  ["bapp_completion", "BAPP Completion"],
];

export default function Performance() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/performance").then(({ data }) => setItems(data.items)).catch(() => {});
  }, []);

  const worst = [...items].sort((a, b) => b.backlog - a.backlog)[0];
  const radarData = METRICS.map(([key, label]) => {
    const row = { metric: label.replace(" Rate", "").replace(" Coverage", " Cov.") };
    items.forEach((i) => (row[i.korwil] = i[key]));
    return row;
  });
  const COLORS = { ARIPIN: "#3b82f6", ADELIA: "#d946ef", ELSA: "#10b981", VIKI: "#f59e0b" };

  return (
    <div data-testid="page-performance">
      <SectionTitle
        title="Korwil Performance"
        subtitle="Perbandingan performa 4 Korwil — Korda dapat langsung melihat yang butuh perhatian"
        right={
          worst && (
            <div className="rounded border border-red-900/70 bg-red-950/40 px-3 py-2 text-xs">
              <span className="text-slate-400">Backlog terbesar: </span>
              <span className="font-bold text-red-300" data-testid="worst-korwil">
                {worst.korwil}
              </span>
              <span className="font-mono-data ml-2 text-red-300">{worst.backlog}</span>
            </div>
          )
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h3 className="mb-3 font-display text-base font-semibold text-white">Perbandingan Metrik (%)</h3>
          <div className="h-72" data-testid="chart-perf-bar">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={items}>
                <CartesianGrid stroke="#1f2937" vertical={false} />
                <XAxis dataKey="korwil" stroke="#6b7280" fontSize={11} />
                <YAxis stroke="#6b7280" fontSize={11} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #374151", borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="completion_rate" name="Completion" fill="#10b981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="on_time_rate" name="On-Time" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="bapp_completion" name="BAPP" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 font-display text-base font-semibold text-white">Radar Performa</h3>
          <div className="h-72" data-testid="chart-perf-radar">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#1f2937" />
                <PolarAngleAxis dataKey="metric" stroke="#6b7280" fontSize={10} />
                {items.map((i) => (
                  <Radar
                    key={i.korwil}
                    dataKey={i.korwil}
                    stroke={COLORS[i.korwil]}
                    fill={COLORS[i.korwil]}
                    fillOpacity={0.15}
                  />
                ))}
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #374151", borderRadius: 6, fontSize: 12 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <TableShell testId="performance-table" empty={items.length === 0}>
        <thead>
          <tr>
            <Th>Korwil</Th>
            <Th className="text-right">Teknisi</Th>
            <Th className="text-right">Terpakai</Th>
            <Th className="text-right">Tech Coverage</Th>
            <Th className="text-right">Unit Coverage</Th>
            <Th className="text-right">Completion</Th>
            <Th className="text-right">On-Time</Th>
            <Th className="text-right">BAPP</Th>
            <Th className="text-right">Backlog</Th>
            <Th className="w-[140px]">Progress</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((k) => (
            <tr key={k.korwil} data-testid={`perf-row-${k.korwil.toLowerCase()}`} className="border-b border-slate-800/70 hover:bg-slate-800/50">
              <Td><KorwilBadge value={k.korwil} /></Td>
              <Td className="text-right font-mono-data text-xs">{k.total_teknisi.toLocaleString("id-ID")}</Td>
              <Td className="text-right font-mono-data text-xs">{k.teknisi_terpakai}</Td>
              <Td className="text-right font-mono-data text-xs text-blue-300">{k.technician_coverage}%</Td>
              <Td className="text-right font-mono-data text-xs text-blue-300">{k.unit_coverage}%</Td>
              <Td className="text-right font-mono-data text-xs text-emerald-300">{k.completion_rate}%</Td>
              <Td className="text-right font-mono-data text-xs text-emerald-300">{k.on_time_rate}%</Td>
              <Td className="text-right font-mono-data text-xs text-amber-300">{k.bapp_completion}%</Td>
              <Td className="text-right font-mono-data text-xs text-red-300">{k.backlog}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <Progress value={k.completion_rate} />
                  <span className="font-mono-data text-[11px] text-slate-400">{k.completion_rate}%</span>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableShell>
    </div>
  );
}
